import { useEffect, useRef, useState } from 'react';
import { savePosterBackground, type PosterBackground } from './mockBackgroundSave';
import { posterThemes } from './themes';

const initial = { themeId: '1', url: posterThemes[0].background };
const same = (a: PosterBackground, b: PosterBackground) => a.themeId === b.themeId && a.url === b.url;

export function usePosterBackground(report: (message: string) => void, setColor: (color: string) => void) {
  const [selection, setSelection] = useState<PosterBackground>(initial);
  const [confirmed, setConfirmed] = useState<PosterBackground>(initial);
  const [customBackground, setCustomBackground] = useState('');
  const [status, setStatus] = useState<'idle' | 'reading' | 'saving' | 'saved' | 'failed'>('idle');
  const custom = useRef('');
  const draft = useRef<PosterBackground>(initial);
  const saved = useRef<PosterBackground>(initial);
  const active = useRef<symbol | null>(null);
  const reader = useRef<FileReader | null>(null);
  const controller = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  function cancel() {
    active.current = null;
    reader.current?.abort(); reader.current = null;
    controller.current?.abort(); controller.current = null;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancel(); };
  }, []);

  async function persist(next: PosterBackground) {
    cancel();
    if (same(next, saved.current)) { setStatus('idle'); return; }
    const job = Symbol('background-save');
    const request = new AbortController();
    active.current = job; controller.current = request;
    setStatus('saving');
    try {
      await savePosterBackground(next, request.signal);
      if (!mounted.current || active.current !== job) return;
      saved.current = next; setConfirmed(next);
      active.current = null; controller.current = null;
      setStatus('saved');
      timer.current = setTimeout(() => {
        if (mounted.current && draft.current === next) setStatus('idle');
        timer.current = null;
      }, 2000);
    } catch {
      if (!mounted.current || active.current !== job) return;
      active.current = null; controller.current = null;
      setStatus('failed');
    }
  }

  function selectTheme(id: string) {
    const theme = posterThemes.find(item => item.id === id);
    if (!theme && (id !== 'custom' || !custom.current)) return;
    const next = { themeId: id, url: theme?.background ?? custom.current };
    if (same(next, draft.current) && status !== 'reading' && status !== 'failed') return;
    report('');
    draft.current = next; setSelection(next);
    if (theme) setColor(theme.color);
    void persist(next);
  }

  function selectImage(files: readonly File[]) {
    const file = files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) { report('背景图片仅支持 JPG、JPEG、PNG 格式。'); return; }
    if (file.size > 5 * 1024 * 1024) { report('背景图片大小不能超过 5 MB。'); return; }
    report(''); cancel();
    const job = Symbol('background-read');
    active.current = job; setStatus('reading');
    const current = new FileReader(); reader.current = current;
    const isCurrent = () => mounted.current && active.current === job;
    function rejectImage(message: string) {
      if (!isCurrent()) return;
      active.current = null; reader.current = null;
      setStatus(same(draft.current, saved.current) ? 'idle' : 'failed');
      report(message);
    }
    current.onerror = () => rejectImage('图片读取失败，请重新选择。');
    current.onload = () => {
      if (!isCurrent()) return;
      if (typeof current.result !== 'string') { rejectImage('图片读取失败，请重新选择。'); return; }
      reader.current = null;
      const url = current.result;
      const image = new Image();
      image.onerror = () => rejectImage('无法识别这张图片，请选择有效的 JPG 或 PNG 图片。');
      image.onload = () => {
        if (!isCurrent()) return;
        if (image.naturalWidth <= 0 || image.naturalHeight <= 0) { rejectImage('无法识别这张图片，请重新选择。'); return; }
        custom.current = url; setCustomBackground(url);
        const next = { themeId: 'custom', url };
        draft.current = next; setSelection(next);
        void persist(next);
      };
      image.src = url;
    };
    current.readAsDataURL(file);
  }
  return { themeId: selection.themeId, background: selection.url, customBackground, confirmed, status, selectTheme, selectImage, retry: () => persist(draft.current) };
}
